// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';

// Services
import { CoreService } from '../../../../shared/services/core.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Models
import { DeviceService, DeviceUnitModel, DeviceUnitViewModel } from '../../../unit-config/services/device.service';
import { AppCleanup } from '../../../../shared/models';
import { StatusType } from '../../../../shared/enums';

// Store
import { DeviceUnitActions } from './device-unit.action';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UtilityHelper } from '../../../../shared/helpers';
import { UnitViewModel } from '../models/unit-view.model';
import { UnitService } from '../services/unit.service';

export interface DeviceUnitStateModel {
	items: DeviceUnitViewModel[];
	loaded: boolean;
}

const defaultState: DeviceUnitStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'deviceUnit',
	defaults: defaultState,
})
@Injectable()
export class DeviceUnitState extends AppCleanup {
	constructor(
		private deviceService: DeviceService,
		private unitService: UnitService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(DeviceUnitActions.GetAll)
	getAll(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.deviceService
			.getDeviceUnits(1)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (deviceUnits) => {
					ctx.setState({
						items: deviceUnits,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.UNIT.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DeviceUnitActions.SetAll)
	setAll(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.SetAll) {
		ctx.setState({
			items: action.deviceUnits,
			loaded: true,
		});
	}

	@Action(DeviceUnitActions.Activate)
	activate(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Activate) {
		let deviceUnits = ctx.getState().items;
		let alreadyActive = this.isAlreadyActive(action.data.unit, deviceUnits);

		if (alreadyActive) {
			const message = `${this.translateService.instant('MODULE.UNIT.ALREADY_ACTIVE')}`;
			this.coreService.toast({ message: message, type: StatusType.WARNING });
		} else {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			let obj: DeviceUnitModel = new DeviceUnitModel();
			obj.deviceId = 1;
			obj.unitGuid = action.data.unit.guid;
			obj.enabled = false;

			if (action.data.unit.unitConfig) {
				obj.enabled = true;
			}

			this.deviceService
				.createDeviceUnit(1, [obj])
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (data) => {
						ctx.dispatch(new DeviceUnitActions.ActivateSuccess());
						ctx.dispatch(new DeviceUnitActions.GetAll({ showSpinner: true, showToast: true }));

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.ACTIVATED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_ACTIVATED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		}
	}

	isAlreadyActive(unit: UnitViewModel, deviceUnits: DeviceUnitViewModel[]) {
		let status = false;

		deviceUnits.forEach((deviceUnit) => {
			if (deviceUnit.unit.id === unit.id) {
				status = true;
			}
		});

		return status;
	}

	@Action(DeviceUnitActions.Deactivate)
	deactivate(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Deactivate) {
		let deactivate = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.deviceService
				.deleteDeviceUnit(action.data.deviceUnit.device.id, action.data.deviceUnit.unit.guid)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						ctx.dispatch(new DeviceUnitActions.Delete(action.data.deviceUnit.id));

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DEACTIVATED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DEACTIVATED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `Deactivate ${this.translateService.instant('MODULE.UNIT.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DEACTIVATE', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE') })}`,
				action: this.translateService.instant('ACTION.DEACTIVATE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deactivate();
					}
				});
		} else {
			deactivate();
		}
	}

	@Action(DeviceUnitActions.DeactivateTemporary)
	deactivateTemporary(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.DeactivateTemporary) {
		let deactivate = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.unitService
				.deleteTemporaryUnit(action.data.deviceUnit.unit.id)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						ctx.dispatch(new DeviceUnitActions.Delete(action.data.deviceUnit.id));
						ctx.dispatch(new DeviceUnitActions.DeactivateTemporarySuccess());
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DEACTIVATED', { name: this.translateService.instant('MODULE.TEMPORARY_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DEACTIVATED', { name: this.translateService.instant('MODULE.TEMPORARY_UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `Deactivate ${this.translateService.instant('MODULE.TEMPORARY_UNIT.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DEACTIVATE', { name: this.translateService.instant('MODULE.TEMPORARY_UNIT.TITLE') })}`,
				action: this.translateService.instant('ACTION.DEACTIVATE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deactivate();
					}
				});
		} else {
			deactivate();
		}
	}

	@Action(DeviceUnitActions.Toggle)
	toggle(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Toggle) {
		let enabling: boolean;
		action.data.deviceUnit.enabled ? (enabling = false) : (enabling = true);

		// if (!confirm) {
		// 	enabling = false;
		// }

		if (enabling && !action.data.deviceUnit?.unit?.unitConfig) {
			const message = `Please, add configuration to the unit before enabling`;
			this.coreService.toast({ message: message, type: StatusType.DANGER });
			return;
		}

		let doToggle = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			let deviceUnits = UtilityHelper.deepCopy(ctx.getState().items);
			let index = deviceUnits.findIndex((du) => du.id === action.data.deviceUnit.id);

			let obj: DeviceUnitModel = new DeviceUnitModel();
			obj.deviceId = 1;
			obj.unitGuid = action.data.deviceUnit?.unit?.guid;
			obj.enabled = !action.data.deviceUnit.enabled;

			this.deviceService
				.updateDeviceUnit(1, action.data.deviceUnit?.unit?.guid, obj)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (data) => {
						ctx.dispatch(new DeviceUnitActions.ToggleSuccess(enabling));

						deviceUnits[index].enabled = !deviceUnits[index].enabled;

						ctx.patchState({
							items: deviceUnits,
							loaded: true,
						});

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							let message: string;

							if (enabling) {
								message = `${this.translateService.instant('GENERIC.ENABLED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							} else {
								message = `${this.translateService.instant('GENERIC.DISABLED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							}

							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							let message: string;

							if (enabling) {
								message = `${this.translateService.instant('GENERIC.NOT_ENABLED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							} else {
								message = `${this.translateService.instant('GENERIC.NOT_DISABLED', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_CAPITAL') })}`;
							}

							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm && !enabling) {
			const confirmModel: ConfirmDialogModel = {
				title: 'Disable unit',
				message: 'Are you sure you want to disable unit?',
				action: this.translateService.instant('ACTION.DISABLE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						doToggle();
					}
				});
		} else {
			doToggle();
		}
	}

	@Action(DeviceUnitActions.Add)
	add(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Add) {
		ctx.setState(
			patch({
				items: append([action.deviceUnit]),
			})
		);
	}

	@Action(DeviceUnitActions.Modify)
	modify(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Modify) {
		const currentState = ctx.getState();
		const existingItem = currentState.items?.find((item) => item.id === action.deviceUnit.id);

		if (existingItem) {
			ctx.setState(
				patch({
					items: updateItem((deviceUnit) => deviceUnit.id === action.deviceUnit.id, action.deviceUnit),
				})
			);
		}
	}

	@Action(DeviceUnitActions.Delete)
	delete(ctx: StateContext<DeviceUnitStateModel>, action: DeviceUnitActions.Delete) {
		const currentState = ctx.getState();
		const existingItem = currentState.items?.find((item) => item.id === action.id);

		if (existingItem) {
			ctx.setState(
				patch({
					items: removeItem((deviceUnit) => deviceUnit.id === action.id),
				})
			);
		}
	}

	@Selector()
	static isLoaded(state: DeviceUnitStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static deviceUnits(state: DeviceUnitStateModel): DeviceUnitViewModel[] {
		return state.items;
	}
}
